import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/lib/content";

const initialForm = {
  nom: "",
  email: "",
  telephone: "",
  societe: "",
  service: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: "idle", msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", msg: "" });
    try {
      await api.post("/devis", form);
      setStatus({
        type: "success",
        msg: "Votre demande a bien été transmise. Nous revenons vers vous rapidement.",
      });
      setForm(initialForm);
    } catch (err) {
      setStatus({
        type: "error",
        msg:
          formatApiErrorDetail(err.response?.data?.detail) ||
          "Une erreur est survenue. Veuillez réessayer.",
      });
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-ink text-white relative overflow-hidden nx-hero-contact" style={{ paddingTop: 200, paddingBottom: 120 }}>
        <div className="absolute inset-0 nx-hero-grid opacity-50"></div>
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #00C8FF 0%, transparent 70%)" }}
        ></div>
        <div className="nx-container relative">
          <div className="max-w-3xl nx-fade-up">
            <span className="nx-overline">Nous contacter</span>
            <h1 className="text-white" style={{ marginTop: 24, marginBottom: 32 }}>
              Parlons de <span style={{ color: "#00C8FF" }}>vos espaces.</span>
            </h1>
            <p className="text-mist" style={{ fontSize: 20, marginBottom: 0 }}>
              Décrivez-nous votre site et vos besoins. Nous vous répondons sous 24h ouvrées avec une proposition claire et adaptée.
            </p>
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="bg-white nx-arch-grid-light" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div className="nx-container">
          <div className="grid grid-cols-1 lg:grid-cols-5" style={{ gap: 30 }}>
            {/* INFO */}
            <div className="lg:col-span-2 nx-fade-up">
              <span className="nx-overline">Coordonnées</span>
              <h2 style={{ marginTop: 24, marginBottom: 32 }}>Une équipe à votre écoute, 7j/7.</h2>

              <ul style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                <li className="flex gap-4" data-testid="info-address">
                  <div className="nx-icon-wrap shrink-0"><MapPin size={22} strokeWidth={1.75} /></div>
                  <div>
                    <div className="nx-overline" style={{ fontSize: 13 }}>Adresse</div>
                    <div className="whitespace-nowrap" style={{ color: "#0D2B45", marginTop: 6 }}>
                      56 avenue Aristide Briand<br />93240 Stains, France
                    </div>
                  </div>
                </li>
                <li className="flex gap-4" data-testid="info-phone">
                  <div className="nx-icon-wrap shrink-0"><Phone size={22} strokeWidth={1.75} /></div>
                  <div>
                    <div className="nx-overline" style={{ fontSize: 13 }}>Téléphone</div>
                    <a href="tel:+33189628787" className="mt-1 block hover:text-cyan" style={{ color: "#0D2B45" }}>01 89 62 87 87</a>
                  </div>
                </li>
                <li className="flex gap-4" data-testid="info-email">
                  <div className="nx-icon-wrap shrink-0"><Mail size={22} strokeWidth={1.75} /></div>
                  <div>
                    <div className="nx-overline" style={{ fontSize: 13 }}>Email</div>
                    <a href="mailto:contact@novanextfacility.fr" className="mt-1 block hover:text-cyan break-all" style={{ color: "#0D2B45" }}>contact@novanextfacility.fr</a>
                  </div>
                </li>
                <li className="flex gap-4" data-testid="info-hours">
                  <div className="nx-icon-wrap shrink-0"><Clock size={22} strokeWidth={1.75} /></div>
                  <div>
                    <div className="nx-overline" style={{ fontSize: 13 }}>Disponibilité</div>
                    <div style={{ color: "#0D2B45", marginTop: 6 }}>Interventions 7j/7 · 24h/24</div>
                  </div>
                </li>
              </ul>

              {/* FAQ link */}
              <div style={{ marginTop: 40, padding: 24, borderRadius: 10, background: "rgba(0,200,255,0.06)", border: "1px solid rgba(0,200,255,0.2)" }}>
                <div className="nx-overline" style={{ marginBottom: 8 }}>Une question avant de nous contacter ?</div>
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-2"
                  style={{ color: "#0099CC", fontFamily: "Exo 2", fontWeight: 600, fontSize: 15 }}
                  data-testid="contact-faq-link"
                >
                  Consulter les questions fréquentes →
                </Link>
              </div>
            </div>

            {/* FORM */}
            <form
              onSubmit={submit}
              className="lg:col-span-3 nx-card nx-fade-up nx-delay-2"
              style={{ padding: 40 }}
              data-testid="devis-form"
            >
              <h3 style={{ marginBottom: 8 }}>Demande de devis</h3>
              <p style={{ fontSize: 16, marginBottom: 32 }}>Remplissez ce formulaire, nous vous recontactons rapidement.</p>

              <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 20 }}>
                <Field label="Nom complet *" testid="input-nom" value={form.nom} onChange={update("nom")} required />
                <Field label="Email *" type="email" testid="input-email" value={form.email} onChange={update("email")} required />
                <Field label="Téléphone *" type="tel" testid="input-telephone" value={form.telephone} onChange={update("telephone")} required minLength={10} />
                <Field label="Société" testid="input-societe" value={form.societe} onChange={update("societe")} />
                <div className="md:col-span-2">
                  <label className="nx-label">Type de service *</label>
                  <select
                    required
                    className="nx-select"
                    value={form.service}
                    onChange={update("service")}
                    data-testid="input-service"
                  >
                    <option value="">Sélectionner un service</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Autre / Multi-services">Autre / Multi-services</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="nx-label">Votre message *</label>
                  <textarea
                    required
                    minLength={5}
                    rows={5}
                    className="nx-textarea"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Précisez votre site, la surface, la fréquence souhaitée, etc."
                    data-testid="input-message"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status.type === "loading"}
                className="nx-btn nx-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ marginTop: 32 }}
                data-testid="devis-submit"
              >
                {status.type === "loading" ? "Envoi en cours..." : "Envoyer la demande"}
                <Send size={18} strokeWidth={2} />
              </button>

              <p style={{ fontSize: 13, color: "#5A6B7A", marginTop: 20, lineHeight: 1.55 }} data-testid="devis-rgpd-notice">
                Les informations recueillies via ce formulaire font l'objet d'un traitement destiné à l'étude de votre demande de devis. Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Consultez notre{" "}
                <Link to="/confidentialite" style={{ color: "#0099CC", fontWeight: 500 }}>politique de confidentialité</Link>.
              </p>

              {status.type === "success" && (
                <div
                  className="flex items-start gap-3 p-4 rounded-[10px] border border-emerald-200 bg-emerald-50 text-emerald-800"
                  style={{ marginTop: 24, fontSize: 15 }}
                  data-testid="devis-success"
                >
                  <CheckCircle2 size={20} strokeWidth={1.75} className="shrink-0 mt-0.5" />
                  <span>{status.msg}</span>
                </div>
              )}
              {status.type === "error" && (
                <div
                  className="flex items-start gap-3 p-4 rounded-[10px] border border-red-200 bg-red-50 text-red-800"
                  style={{ marginTop: 24, fontSize: 15 }}
                  data-testid="devis-error"
                >
                  <AlertTriangle size={20} strokeWidth={1.75} className="shrink-0 mt-0.5" />
                  <span>{status.msg}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="nx-label">{label}</label>
      <input {...rest} data-testid={testid} className="nx-input" />
    </div>
  );
}
